// 📄 versionService.ts
// Versiones / entregas del proyecto (plan-proyectos-generales.md, G6): cada
// salida (a QAS, a producción…) con lo que incluye: solicitudes de cambio
// aprobadas y tareas. Al liberarla se pueden marcar sus cambios como
// implementados, y genera las notas de versión para compartir con el cliente.
// Lo ven todos los miembros (también el cliente); lo edita el equipo.
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  arrayUnion,
  Timestamp,
  serverTimestamp
} from "firebase/firestore";
import { db } from '../firebase';
import { Cambio, cambiarEstado } from './cambioService.ts';
import { Tarea } from './tareaService.ts';

export type VersionEstado = 'Planificada' | 'En pruebas' | 'Liberada' | 'Cancelada';
export const ESTADOS_VERSION: VersionEstado[] = ['Planificada', 'En pruebas', 'Liberada', 'Cancelada'];
export const AMBIENTES_VERSION = ['DEV', 'QAS', 'PRD', 'Otro'];

export interface ItemVersion {
  id: string;
  titulo: string;
}

export interface VersionFormData {
  numero: string; // ej. "v1.2.0"
  titulo: string; // ej. "Pagos en caja"
  notas: string; // texto libre adicional para las notas de versión
  ambiente: string;
  fechaPlanificada: string; // YYYY-MM-DD
  cambios: ItemVersion[];
  tareas: ItemVersion[];
}

export interface Version extends VersionFormData {
  id: string;
  estado: VersionEstado;
  fechaLiberacion: string; // YYYY-MM-DD, al liberarla
  creadoPor: string;
  creadoPorNombre: string;
  creadoEn: any;
  historial: { tipo: string; actorNombre: string; en: any }[];
}

const versionesRef = (projectId: string) => collection(db, "projects", projectId, "versiones");
const versionRef = (projectId: string, id: string) => doc(db, "projects", projectId, "versiones", id);
// Fecha LOCAL (no UTC): de noche en América, toISOString ya da el día siguiente.
const hoyStr = (): string => new Date().toLocaleDateString('en-CA');
const evento = (tipo: string, actorNombre: string) => ({ tipo, actorNombre, en: Timestamp.now() });

/** Cambios que se pueden incluir: aprobados en adelante (no rechazados ni cancelados). */
export const cambiosIncluibles = (cambios: Cambio[]) =>
  cambios.filter((c) => ['Aprobada', 'En implementación', 'Implementada'].includes(c.estado));

export const getVersiones = async (projectId: string): Promise<Version[]> => {
  const snap = await getDocs(versionesRef(projectId));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<Version, "id">) }))
    .sort((a, b) => (b.creadoEn?.toMillis?.() || 0) - (a.creadoEn?.toMillis?.() || 0));
};

const validar = (data: VersionFormData) => {
  if (!data.numero.trim()) throw new Error("El número de versión es obligatorio (ej. v1.0.0).");
};

// El título se muestra como "v1.0.0 · Título": se quitan separadores que el usuario haya escrito al inicio.
const limpiarTitulo = (t: string) => t.trim().replace(/^[·\-–—|:]+\s*/, '');

export const createVersion = async (projectId: string, data: VersionFormData, uid: string, nombre: string): Promise<string> => {
  validar(data);
  const ref = doc(versionesRef(projectId));
  await setDoc(ref, {
    ...data,
    numero: data.numero.trim(),
    titulo: limpiarTitulo(data.titulo),
    notas: data.notas.trim(),
    estado: 'Planificada' as VersionEstado,
    fechaLiberacion: '',
    creadoPor: uid,
    creadoPorNombre: nombre,
    creadoEn: serverTimestamp(),
    historial: [evento('creada', nombre)]
  });
  return ref.id;
};

export const updateVersion = async (projectId: string, id: string, data: VersionFormData, nombre: string) => {
  validar(data);
  await updateDoc(versionRef(projectId, id), {
    ...data,
    numero: data.numero.trim(),
    titulo: limpiarTitulo(data.titulo),
    notas: data.notas.trim(),
    historial: arrayUnion(evento('editada', nombre))
  });
};

export const cambiarEstadoVersion = async (projectId: string, id: string, estado: VersionEstado, nombre: string) => {
  await updateDoc(versionRef(projectId, id), {
    estado,
    ...(estado === 'Liberada' ? { fechaLiberacion: hoyStr() } : {}),
    historial: arrayUnion(evento(estado, nombre))
  });
};

/**
 * Libera la versión y, si se pide, marca como Implementada cada cambio
 * incluido que todavía no lo esté (mismo historial de la solicitud).
 */
export const liberarVersion = async (
  projectId: string, version: Version, cambios: Cambio[], marcarCambios: boolean, uid: string, nombre: string
) => {
  await cambiarEstadoVersion(projectId, version.id, 'Liberada', nombre);
  if (!marcarCambios) return;
  const ids = new Set(version.cambios.map((c) => c.id));
  for (const c of cambios) {
    if (ids.has(c.id) && c.estado !== 'Implementada' && ['Aprobada', 'En implementación'].includes(c.estado)) {
      await cambiarEstado(projectId, c.id, 'Implementada', uid, nombre, `incluido en ${version.numero}`);
    }
  }
};

export const deleteVersion = async (projectId: string, id: string) => {
  await deleteDoc(versionRef(projectId, id));
};

/** Notas de versión en texto plano, para copiar y enviar al cliente. */
export const notasDeVersion = (v: Version, proyectoNombre: string, tareas: Tarea[] = []): string => {
  const lineas = [
    `${proyectoNombre} · ${v.numero}${v.titulo ? ` · ${limpiarTitulo(v.titulo)}` : ''}`,
    `${v.estado === 'Liberada' ? `Liberada el ${v.fechaLiberacion}` : `Estado: ${v.estado}${v.fechaPlanificada ? ` (planificada para el ${v.fechaPlanificada})` : ''}`}${v.ambiente ? ` en ${v.ambiente}` : ''}`,
    ''
  ];
  if (v.notas) lineas.push(v.notas, '');
  if (v.cambios.length) {
    lineas.push('Cambios incluidos:');
    v.cambios.forEach((c) => lineas.push(`- ${c.titulo}`));
    lineas.push('');
  }
  if (v.tareas.length) {
    lineas.push('Trabajo realizado:');
    v.tareas.forEach((t) => {
      const actual = tareas.find((x) => x.id === t.id);
      lineas.push(`- ${t.titulo}${actual && actual.estado !== 'Completada' ? ` (${actual.estado.toLowerCase()})` : ''}`);
    });
  }
  return lineas.join('\n').trim();
};
